import React from "react";
import { Monitor, Moon, Sun } from "lucide-react";

// Sister sites in the app.nz network. Rendered in the footer on every marketing
// and auth page for cross-linking / SEO. Kept here (not inlined in PlatformHome)
// so the footer stays a single shared component across the whole site.
const NETWORK_LINKS: Array<{ href: string; label: string }> = [
  { href: "https://gpubrain.app.nz", label: "GpuBrain" },
  { href: "/papers", label: "Papers" },
  { href: "https://readingtime.app.nz", label: "Reading Time" },
  { href: "https://mojojojo.app.nz", label: "mojojojo" },
  { href: "https://netwrck.com", label: "Netwrck" },
  { href: "https://text-generator.io", label: "Text-Generator.io" },
  { href: "https://codex-infinity.com", label: "Codex Infinity" },
  { href: "https://openpaths.io", label: "OpenPaths" },
  { href: "https://cutedsl.cc", label: "CuteDSL" },
  { href: "https://ebank.nz", label: "AI Art Generator" },
  { href: "https://aiart-generator.art", label: "AIArt-Generator.art" },
  { href: "https://sitesim.net", label: "SiteSim" },
  { href: "https://simplexgen.com", label: "SimplexGen" },
  { href: "https://dictatorflow.com", label: "DictatorFlow" },
  { href: "https://webfiddle.net", label: "WebFiddle" },
  { href: "https://ring.nz", label: "Ring.nz" },
  { href: "https://chatgibidy.com", label: "ChatGibidy" },
  { href: "https://bitbank.nz", label: "BitBank" },
  { href: "https://experimentflow.com", label: "ExperimentFlow" },
  { href: "https://evangeler.com", label: "Evangeler" },
  { href: "https://hires.nz", label: "Hires.nz" },
  { href: "https://how.nz", label: "How.nz" },
  { href: "https://v5games.com", label: "V5 Games" },
  { href: "https://addictingwordgames.com", label: "Addicting Word Games" },
  { href: "https://bigmultiplayerchess.com", label: "Big Multiplayer Chess" },
  { href: "https://wordsmashing.com", label: "Word Smashing" },
  { href: "https://rewordgame.com", label: "reWord Game" },
  { href: "https://multiplicationmaster.com", label: "Multiplication Master" },
];

// Product columns. Every href is a real route (App.tsx / platformRoutes.ts) —
// the footer doubles as the sitemap, so no dead marketing stubs.
const COLUMNS: Array<{ title: string; links: Array<{ href: string; label: string }> }> = [
  {
    title: "Agent stack",
    links: [
      { href: "/agent", label: "Cloud coding agent" },
      { href: "/agents-sdk", label: "Agents SDK" },
      { href: "/integrations", label: "Integrations" },
      { href: "/browser-agent", label: "Browser agent" },
      { href: "/auto-agents", label: "Monitors & auto-agents" },
      { href: "/schedulers", label: "Schedulers" },
      { href: "/skills", label: "Agent skills" },
      { href: "/mcp-servers", label: "MCP servers" },
      { href: "/deep-research", label: "Deep research" },
    ],
  },
  {
    title: "Models & API",
    links: [
      { href: "/gateway", label: "AI Gateway" },
      { href: "/models", label: "Model catalog" },
      { href: "/evals", label: "Model evals" },
      { href: "/spaces", label: "Model spaces" },
      { href: "/gateway#playground", label: "Playground" },
      { href: "/text-to-image", label: "Text to image" },
      { href: "/image-to-3d", label: "Image to 3D" },
      { href: "/text-to-3d", label: "Text to 3D" },
      { href: "/music", label: "Music & SFX" },
      { href: "/audio", label: "Audio editor" },
      { href: "/media-optimizer", label: "Media optimizer" },
      { href: "/art", label: "AI art & library" },
      { href: "/chat", label: "Chat" },
      { href: "/docs", label: "API reference" },
      { href: "/schema", label: "Schema" },
      { href: "/providers", label: "Become a provider" },
    ],
  },
  {
    title: "Compute & hosting",
    links: [
      { href: "/deploys", label: "Deploys" },
      { href: "/addons", label: "Addons" },
      { href: "/addons/postgres", label: "Postgres hosting" },
      { href: "/addons/gobed", label: "gobed vector search" },
      { href: "/sites", label: "Site hosting" },
      { href: "/analytics", label: "Analytics" },
      { href: "/cogs", label: "Cog GPU hosting" },
      { href: "/rl", label: "RL training" },
      { href: "/builds", label: "Builds & CI" },
      { href: "/workers", label: "Workers" },
      { href: "/queues", label: "Task queues" },
      { href: "/domains", label: "Domains" },
      { href: "/repos", label: "Git hosting" },
    ],
  },
  {
    title: "Tools",
    links: [
      { href: "/tools", label: "AI tools" },
      { href: "/draw", label: "Draw" },
      { href: "/diffusion", label: "Diffusion canvas" },
      { href: "/livedraw", label: "Live Draw" },
      { href: "/write", label: "Write" },
      { href: "/sheets", label: "Sheets" },
      { href: "/artifacts", label: "Artifacts" },
      { href: "/studio", label: "Video studio" },
      { href: "/notebooks", label: "Notebooks" },
      { href: "/datasets", label: "Datasets" },
    ],
  },
  {
    title: "Learn",
    links: [
      { href: "/docs", label: "Docs" },
      { href: "/blog", label: "Blog" },
      { href: "/evals", label: "Eval guides" },
      { href: "/prompts", label: "Prompt library" },
      { href: "/cli", label: "CLI" },
      { href: "/alternatives", label: "Alternatives" },
      { href: "/papers", label: "Papers" },
      { href: "/characters", label: "AI characters" },
      { href: "/art", label: "Art gallery" },
      { href: "/security", label: "Security" },
      { href: "/consulting", label: "Consulting" },
    ],
  },
  {
    title: "Company",
    links: [
      { href: "/pricing", label: "Pricing" },
      { href: "/enterprise", label: "Enterprise" },
      { href: "/settings", label: "Settings" },
      { href: "/billing", label: "Billing" },
      { href: "/status", label: "Status" },
      { href: "/investors", label: "Investors" },
      { href: "/signup", label: "Create account" },
      { href: "/terms", label: "Terms of Service" },
      { href: "/privacy", label: "Privacy Policy" },
    ],
  },
];

const THEMES: Array<{ id: string; label: string; icon: React.ComponentType<{ size?: number }> }> = [
  { id: "system", label: "System theme", icon: Monitor },
  { id: "dark", label: "Dark theme", icon: Moon },
  { id: "light", label: "Light theme", icon: Sun },
];

// Theme selector (Vercel-style segmented control). Dark is the site default;
// the choice persists in localStorage and is applied before first paint by the
// inline boot script in index.html. Renders "dark" on the server and hydrates
// the stored value in an effect so SSR markup always matches.
function ThemeSelect() {
  const [theme, setTheme] = React.useState("dark");

  React.useEffect(() => {
    try {
      setTheme(localStorage.getItem("appnz_theme") || "dark");
    } catch {
      /* storage blocked */
    }
  }, []);

  React.useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const apply = () => {
      document.documentElement.dataset.theme = mq.matches ? "light" : "dark";
    };
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [theme]);

  const pick = (t: string) => {
    setTheme(t);
    try {
      localStorage.setItem("appnz_theme", t);
    } catch {
      /* storage blocked */
    }
    const resolved =
      t === "system"
        ? window.matchMedia("(prefers-color-scheme: light)").matches
          ? "light"
          : "dark"
        : t;
    document.documentElement.dataset.theme = resolved;
  };

  return (
    <div
      className="inline-flex items-center rounded-full border border-white/12 p-0.5"
      role="radiogroup"
      aria-label="Select a display theme"
      data-testid="theme-select"
    >
      {THEMES.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          role="radio"
          aria-checked={theme === id}
          aria-label={label}
          title={label}
          onClick={() => pick(id)}
          data-testid={`theme-${id}`}
          className={`rounded-full p-1.5 transition-colors ${
            theme === id ? "bg-white/12 text-white" : "text-white/65 hover:text-white"
          }`}
        >
          <Icon size={14} />
        </button>
      ))}
    </div>
  );
}

// SiteFooter is the shared marketing footer shown on every marketing + auth page
// (home, platform routes, docs, gateway, login/signup, etc.). It is rendered once
// from the Marketing wrapper in App.tsx so coverage stays consistent.
export function SiteFooter() {
  return (
    <footer className="px-5 py-8 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-[1800px] border-t border-white/10 pt-8">
        <div className="grid gap-x-6 gap-y-8 text-sm text-white/65 sm:grid-cols-3 lg:grid-cols-6">
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <div className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-white/60">
                {col.title}
              </div>
              <div className="grid gap-2">
                {col.links.map((l) => (
                  <a key={`${col.title}-${l.href}`} href={l.href} className="hover:text-white">
                    {l.label}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col justify-between gap-6 border-t border-white/10 pt-6 sm:flex-row sm:items-start">
          <div className="max-w-sm">
            <a
              href="/"
              className="inline-flex items-center gap-2 font-display text-lg font-bold text-white"
            >
              <img
                src="/assets/app-logo-64.webp"
                alt="app.nz"
                className="h-7 w-7 object-contain"
                width="64"
                height="64"
              />
              app.nz
            </a>
            <p className="mt-3 text-sm leading-6 text-white/65">
              The agent cloud for shipping software on app.nz.
            </p>
            <p className="mt-3 text-sm leading-6 text-white/65">
              Built in New Zealand by App AI NZ.
            </p>
          </div>
          <div className="grid gap-2 text-sm text-white/65">
            <div className="mb-1 text-xs font-bold uppercase tracking-[0.16em] text-white/60">
              Social
            </div>
            <a href="https://x.com/leepenkman" className="hover:text-white">
              X / Twitter
            </a>
            <a href="https://github.com/lee101" className="hover:text-white">
              GitHub
            </a>
            <a
              href="https://www.youtube.com/channel/UC99WzS0KTNLl2hbx-xXA5lg"
              className="hover:text-white"
            >
              YouTube
            </a>
          </div>
        </div>

        <div className="mt-10 border-t border-white/10 pt-6">
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-white/60">
            The app.nz network
          </div>
          <div className="grid gap-x-6 gap-y-2 text-[13px] text-white/65 sm:grid-cols-3 lg:grid-cols-5">
            {NETWORK_LINKS.map((l) => (
              <a key={l.href} href={l.href} className="hover:text-white">
                {l.label}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-8 flex flex-col justify-between gap-3 border-t border-white/10 pt-6 text-xs text-white/60 sm:flex-row sm:items-center">
          <span>
            © {new Date().getFullYear()} App AI NZ Ltd. All rights reserved.
          </span>
          <span className="flex flex-wrap items-center gap-3">
            <a href="/status" className="inline-flex items-center gap-1.5 hover:text-white">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden />
              All systems normal
            </a>
            <a href="/terms" className="hover:text-white">
              Terms
            </a>
            <a href="/privacy" className="hover:text-white">
              Privacy
            </a>
            <ThemeSelect />
          </span>
        </div>
      </div>
    </footer>
  );
}

export default SiteFooter;
